import { z } from "zod";
import type { Schemas } from "../workspace/api.generated";

export const adminLoginSchema = z.object({
  secondFactorMode: z.enum(["totp", "recovery"]),
  loginId: z
    .string()
    .trim()
    .min(1, "관리자 계정을 입력해 주세요.")
    .max(100, "관리자 계정은 100자 이하로 입력해 주세요."),
  password: z
    .string()
    .min(1, "비밀번호를 입력해 주세요.")
    .max(200, "비밀번호는 200자 이하로 입력해 주세요."),
  totpCode: z
    .string()
    .trim()
    .refine((value) => value === "" || /^\d{6}$/.test(value), "TOTP 코드는 숫자 6자리로 입력해 주세요."),
  recoveryCode: z.string().trim().max(64, "복구코드는 64자 이하로 입력해 주세요."),
}).superRefine((values, context) => {
  if (values.secondFactorMode === "recovery" && values.recoveryCode.length < 8) {
    context.addIssue({
      code: "custom",
      message: "보관한 복구코드를 입력해 주세요.",
      path: ["recoveryCode"],
    });
  }
});

export type AdminLoginFormValues = z.infer<typeof adminLoginSchema>;

const PASSWORD_POLICY_MESSAGE = "8~30자이며 영문 대문자, 소문자, 숫자, 특수문자를 모두 포함해 주세요.";

function meetsAdminPasswordPolicy(value: string): boolean {
  return value.length >= 8
    && value.length <= 30
    && /\p{Lu}/u.test(value)
    && /\p{Ll}/u.test(value)
    && /\p{Nd}/u.test(value)
    && Array.from(value).some((character) => !/[\p{L}\p{N}]/u.test(character));
}

export const adminPasswordChangeSchema = z.object({
  currentPassword: z
    .string()
    .min(1, "현재 비밀번호를 입력해 주세요.")
    .max(200, "현재 비밀번호는 200자 이하로 입력해 주세요."),
  newPassword: z.string().refine(meetsAdminPasswordPolicy, PASSWORD_POLICY_MESSAGE),
  newPasswordConfirmation: z.string().min(1, "새 비밀번호 확인을 입력해 주세요."),
}).superRefine((values, context) => {
  if (values.newPassword === values.currentPassword) {
    context.addIssue({
      code: "custom",
      message: "현재 비밀번호와 다른 비밀번호를 입력해 주세요.",
      path: ["newPassword"],
    });
  }
  if (values.newPassword !== values.newPasswordConfirmation) {
    context.addIssue({
      code: "custom",
      message: "새 비밀번호가 일치하지 않습니다.",
      path: ["newPasswordConfirmation"],
    });
  }
});

export type AdminPasswordChangeFormValues = z.infer<typeof adminPasswordChangeSchema>;

export const adminTotpConfirmationSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "TOTP 코드는 숫자 6자리로 입력해 주세요."),
});

export type AdminTotpConfirmationFormValues = z.infer<typeof adminTotpConfirmationSchema>;

export function toAuthenticateAdminRequest(
  values: AdminLoginFormValues,
): Schemas["AuthenticateAdminRequest"] {
  return {
    loginId: values.loginId,
    password: values.password,
    totpCode: values.secondFactorMode === "totp" ? values.totpCode || null : null,
    recoveryCode: values.secondFactorMode === "recovery" ? values.recoveryCode || null : null,
  };
}

export function normalizePastedTotpCode(value: string): string | null {
  const compact = value.replace(/\s/g, "");
  return /^\d{6}$/.test(compact) ? compact : null;
}

export function toChangeAdminPasswordRequest(
  values: AdminPasswordChangeFormValues,
): Schemas["ChangeAdminPasswordRequest"] {
  return {
    currentPassword: values.currentPassword,
    newPassword: values.newPassword,
  };
}
